import { DataTableProps } from "@aidkitorg/component-library";
import { TargetFieldCategoryValue } from "@aidkitorg/types/lib/admin-panel/settings";
import { DataTableRow } from "./types";

// Generate consistent color for string values: same logic as in distro editor
export const stringToColour = (str: string) => {
  if (!str) return 'hsl(0%,0%,0%)';
    
  var hash = 0;
  for (var i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  var colour = 'hsl(' + (hash % 360) + ', 60%, 70%)';
  return colour;
}

// Calculate relative luminance and return appropriate tinted text color for WCAG contrast
export const getContrastTextColor = (backgroundColor: string): string => {
  // Convert HSL to RGB
  const hslMatch = backgroundColor.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
  if (!hslMatch) return '#000000';
  
  const hue = parseInt(hslMatch[1]);
  const saturation = parseInt(hslMatch[2]);
  const lightness = parseInt(hslMatch[3]);
  
  // Convert to RGB for luminance calculation
  const h = hue / 360;
  const s = saturation / 100;
  const l = lightness / 100;
  
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };
  
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const r = hue2rgb(p, q, h + 1/3);
  const g = hue2rgb(p, q, h);
  const b = hue2rgb(p, q, h - 1/3);
  
  // Calculate relative luminance using WCAG formula
  const sRGBtoLin = (colorChannel: number) => {
    return colorChannel <= 0.03928 ? colorChannel / 12.92 : Math.pow((colorChannel + 0.055) / 1.055, 2.4);
  };
  
  const luminance = 0.2126 * sRGBtoLin(r) + 0.7152 * sRGBtoLin(g) + 0.0722 * sRGBtoLin(b);
  
  // Create tinted text color using the same hue but adjusted lightness and saturation
  if (luminance > 0.179) {
    // Background is light, use dark tinted text with more noticeable tint
    return `hsl(${hue}, ${Math.min(saturation + 40, 100)}%, 16%)`;
  } else {
    // Background is dark, use light tinted text with more noticeable tint
    return `hsl(${hue}, ${Math.max(saturation - 10, 40)}%, 90%)`;
  }
}

// Category options for the dropdown
export const CATEGORY_OPTIONS: readonly { label: string, value: TargetFieldCategoryValue }[] = [
  { value: '', label: 'No Category' },
  { value: 'personal', label: 'Personal Information' },
  { value: 'address', label: 'Address Information' },
  { value: 'identity', label: 'Identity Documents' },
  { value: 'selfie', label: 'Selfie & Liveness Verification' },
  { value: 'status', label: 'Application Status & Timeline' },
  { value: 'verification', label: 'Verification Flags & Errors' },
  { value: 'cross_program', label: 'Cross-Program & Duplicate Detection' },
  { value: 'income', label: 'Income & Eligibility' },
  { value: 'risk', label: 'IP & Email Risk' },
  { value: 'bank', label: 'Bank Account Verification' },
  { value: 'notifications', label: 'Notifications & Communications' }
] as const;

// Custom filter function for the target field table in ManageFieldsPage
export const filterTargetFieldRows: DataTableProps<any>['globalFilterFn'] = (row, _columnId, filterValue) => {
  const data = row.original as DataTableRow;
  const searchText = filterValue.toLowerCase();

  // Search in target field
  if (data.targetField.toLowerCase().includes(searchText)) {
    return true;
  }

  // Search in related fields
  if (data.relatedFields.some(field => field.toLowerCase().includes(searchText))) {
    return true;
  }

  // Search in display name and description
  if (data.displayName?.toLowerCase().includes(searchText)) {
    return true;
  }

  if (data.description?.toLowerCase().includes(searchText)) {
    return true;
  }

  // Search in category (both value and label)
  if (data.category?.toLowerCase().includes(searchText)) {
    return true;
  }

  // Search in category label
  const categoryOption = CATEGORY_OPTIONS.find(opt => opt.value === data.category);
  if (categoryOption?.label.toLowerCase().includes(searchText)) {
    return true;
  }

  // Search in kind
  if (data.kind?.toLowerCase().includes(searchText)) {
    return true;
  }

  return false;
}